import React, { useCallback, useEffect, useState } from 'react'
import {
  Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Table, Tag, Tooltip, Typography, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DownloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { get, patch, post } from '../../lib/request'
import {
  BusinessFormat,
  INTENT_CATEGORY_LABELS,
  PAY_METHOD_LABELS,
  REWARD_REVIEW_LABELS,
  RewardItem,
  SETTLEMENT_STATUS_LABELS,
  downloadCsv,
  errMsg,
  fmtDate,
  fmtMoney,
  fmtTime,
} from '../../lib/api'
import { DocumentUploadList } from '../BenefitConfig/shared'
import { useAuthStore } from '../../stores/auth'
import { hasPermission } from '../../utils/permissions'

const { Text } = Typography

/**
 * 答谢管理（原「奖励管理」，full_feature_v3 → thankyou_v2 批次 8.2）
 * 待发放列表（发放状态机过滤）+ 一键核算 + 答谢调整（仅财务经理，reason 必填留痕）
 * + 审批 / 发放（409 DEPOSIT_NOT_CONFIRMED 闸门提示）+ 签收存档 + 台账导出
 *
 * thankyou_v1（批次 2）：列表在「核算金额」后增成交业态 / 推荐人答谢 / 被推荐人礼遇三列
 * （数字金额列全保留），调整弹窗增两段文字域。
 *
 * thankyou_v2（批次 8.2，设计契约 planning/design-assets-v2/admin-rewards-three-column.html）：
 * - 单列「应发金额」拆为财务三栏：应发(税前) grossAmount / 代扣个税(20%) taxAmount / 实发 netAmount；
 *   入驻商家渠道（displayMode==='coupon'）三栏恒 ¥0 且置灰（金额口径 = 券采购额，PDF 7.1/7.2）
 * - 新增「套档结果」couponTierResult、「券采购额」couponAmount、「采购单号/发票号」合并列
 * - 操作列增「券发放登记」弹窗 → POST /admin/rewards/:id/coupon-issue
 * - 「调整金额」→「调整答谢」，弹窗 TextArea 三段（调整原因 / 推荐人答谢 / 被推荐人礼遇）
 */
const RewardsPage: React.FC = () => {
  const role = useAuthStore((s) => s.user?.role)
  // thankyou_v2：券发放登记弹窗的「发放人（自动当前账号）」只读展示
  const currentUser = useAuthStore((s) => s.user)
  const isFinanceManager = hasPermission(role, 'REWARD_ADJUST_AMOUNT')
  const canApprove = hasPermission(role, 'REWARD_APPROVE')
  const canOperate = hasPermission(role, 'REWARD_OPERATE')

  const [list, setList] = useState<RewardItem[]>([])
  const [loading, setLoading] = useState(false)
  const [reviewStatus, setReviewStatus] = useState<string | undefined>(undefined)
  const [calcLoading, setCalcLoading] = useState(false)
  const [exporting, setExporting] = useState(false)

  const [amountTarget, setAmountTarget] = useState<RewardItem | null>(null)
  const [amountForm] = Form.useForm<{
    // 现金线索字段
    grossAmount?: number
    netAmount?: number
    // 商家券线索字段
    couponAmount?: number
    couponTierResult?: string
    // 公共字段
    reason: string
    referrerRewardText?: string
    referredBenefitText?: string
  }>()

  // thankyou_v2（批次 8.2）：券发放登记弹窗（发放人自动取当前账号，前端只读）
  const [couponTarget, setCouponTarget] = useState<RewardItem | null>(null)
  const [couponForm] = Form.useForm<{
    couponIssuedAt?: dayjs.Dayjs | null
    couponNoOrProof?: string[]
    settlementStatus?: string
  }>()

  const [payTarget, setPayTarget] = useState<RewardItem | null>(null)
  const [payForm] = Form.useForm<{ payMethod: 'transfer' | 'cash'; transactionNo?: string; signedAt?: dayjs.Dayjs }>()

  const [receiptTarget, setReceiptTarget] = useState<RewardItem | null>(null)
  const [receiptForm] = Form.useForm<{ receiptUrl: string }>()
  const [submitting, setSubmitting] = useState(false)

  // thankyou_v1（批次 2）：成交业态 code→中文名本地映射（后端 dealCategoryLabel 优先，此为兜底）
  const [formatLabels, setFormatLabels] = useState<Record<string, string>>({})

  useEffect(() => {
    get<BusinessFormat[]>('/admin/business-formats')
      .then((d) => {
        const map: Record<string, string> = {}
        ;(Array.isArray(d) ? d : []).forEach((f) => { map[f.code] = f.name })
        setFormatLabels(map)
      })
      // 读失败不阻断本页（后端 dealCategoryLabel 仍可用），仅降级到共享内置映射
      .catch(() => setFormatLabels({}))
  }, [])

  const fetchList = useCallback(() => {
    setLoading(true)
    // includePending=1：把「已核验 + 已成交但尚未生成奖励单」的线索作为待核算虚拟行一并取回，
    // 否则奖励单生成前财务侧看不到任何待办（这正是本次修复的核心诉求）。
    get<RewardItem[]>('/admin/rewards', {
      params: { includePending: '1', ...(reviewStatus ? { reviewStatus } : {}) },
    })
      .then((d) => setList(Array.isArray(d) ? d : []))
      .catch((err) => {
        message.warning(errMsg(err, '奖励列表加载失败（后端可能未启动）'))
        setList([])
      })
      .finally(() => setLoading(false))
  }, [reviewStatus])

  useEffect(() => { fetchList() }, [fetchList])

  const handleCalc = () => {
    Modal.confirm({
      title: '一键核算答谢',
      content: '对「已成交且两押一租已到账、尚未生成答谢单」的线索批量核算（标准 / 商会：应发 = 首月租金，代扣个税 20%；入驻商家：三栏 0，券采购额按成交面积套档）。确认执行？',
      okText: '开始核算',
      cancelText: '取消',
      onOk: async () => {
        setCalcLoading(true)
        try {
          const r = await post<{ created: number; skipped: number }>('/admin/rewards/calc')
          message.success(`核算完成：新建 ${r.created} 条，跳过 ${r.skipped} 条`)
          fetchList()
        } catch (err: unknown) {
          message.error(errMsg(err, '核算失败'))
        } finally {
          setCalcLoading(false)
        }
      },
    })
  }

  const handleExport = () => {
    setExporting(true)
    downloadCsv('/admin/rewards/export', 'rewards.csv')
      .catch((err: unknown) => message.error(errMsg(err, '导出失败')))
      .finally(() => setExporting(false))
  }

  const submitAmount = async () => {
    if (!amountTarget) return
    const v = await amountForm.validateFields()
    setSubmitting(true)
    try {
      const coupon = isCouponChannel(amountTarget)
      if (coupon) {
        // 商家券线索：小程序券卡读 couponAmount/couponTierResult，弹窗只改这两项；
        // amount 回填当前值（仅为通过后端必填校验），不动现金口径。
        await patch(`/admin/rewards/${amountTarget.id}/amount`, {
          amount: Number(amountTarget.amount),
          reason: v.reason,
          couponAmount: v.couponAmount,
          couponTierResult: v.couponTierResult ?? null,
          referrerRewardText: v.referrerRewardText ?? null,
          referredBenefitText: v.referredBenefitText ?? null,
        })
      } else {
        // 现金线索：小程序金额卡读 grossAmount/netAmount（个税 20% 口径 tax=gross-net 自动算）；
        // amount 与 grossAmount 同值，避免两字段长期分叉。三段文字可同时调整（后端各写一条 reward_logs）。
        const gross = Number(v.grossAmount)
        const net = Number(v.netAmount)
        await patch(`/admin/rewards/${amountTarget.id}/amount`, {
          amount: gross,
          grossAmount: gross,
          netAmount: net,
          taxAmount: Math.round((gross - net) * 100) / 100,
          reason: v.reason,
          referrerRewardText: v.referrerRewardText ?? null,
          referredBenefitText: v.referredBenefitText ?? null,
        })
      }
      message.success('答谢已调整并留痕')
      setAmountTarget(null)
      fetchList()
    } catch (err: unknown) {
      message.error(errMsg(err, '调整失败'))
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * 券发放登记（thankyou_v2 批次 8.2）→ POST /admin/rewards/:id/coupon-issue。
   * 发放人由后端取当前账号（前端只读展示，不上送）；券号或凭证走 /admin/uploads/document。
   */
  const submitCouponIssue = async () => {
    if (!couponTarget) return
    const v = await couponForm.validateFields()
    setSubmitting(true)
    try {
      const proofs = v.couponNoOrProof ?? []
      await post(`/admin/rewards/${couponTarget.id}/coupon-issue`, {
        couponIssuedAt: v.couponIssuedAt ? v.couponIssuedAt.format('YYYY-MM-DD HH:mm:ss') : undefined,
        couponNoOrProof: proofs.length > 0 ? proofs[0] : undefined,
        settlementStatus: v.settlementStatus ?? 'unsettled',
      })
      message.success('券发放已登记')
      setCouponTarget(null)
      fetchList()
    } catch (err: unknown) {
      message.error(errMsg(err, '券发放登记失败'))
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * 成交业态归一（三源回落）：后端中文 dealCategoryLabel 优先；其次本地业态映射（含后台新增业态）；
   * 最后共享内置映射 INTENT_CATEGORY_LABELS 兜底（office/rental/retail）。
   * code 源：真实行在 lead.deal.dealCategory，待核算虚拟行在顶层 dealCategory。
   */
  const dealCategoryText = (r: RewardItem): string => {
    if (r.dealCategoryLabel) return r.dealCategoryLabel
    const code = r.dealCategory ?? r.lead?.deal?.dealCategory
    if (!code) return '-'
    return formatLabels[code] ?? INTENT_CATEGORY_LABELS[code] ?? code
  }

  const handleApprove = (r: RewardItem) => {
    Modal.confirm({
      title: '审批答谢',
      content: `确认审批通过答谢单 #R-${r.id}（应发税前 ${fmtMoney(r.grossAmount ?? r.amount)}）？审批后进入待发放。`,
      okText: '审批通过',
      cancelText: '取消',
      onOk: () =>
        post(`/admin/rewards/${r.id}/approve`)
          .then(() => { message.success('已审批通过'); fetchList() })
          .catch((err: unknown) => message.error(errMsg(err, '审批失败'))),
    })
  }

  const submitPay = async () => {
    if (!payTarget) return
    const v = await payForm.validateFields()
    setSubmitting(true)
    try {
      await post(`/admin/rewards/${payTarget.id}/pay`, {
        payMethod: v.payMethod,
        transactionNo: v.transactionNo,
        signedAt: v.signedAt ? v.signedAt.format('YYYY-MM-DD') : undefined,
      })
      message.success('答谢已发放')
      setPayTarget(null)
      fetchList()
    } catch (err: unknown) {
      // 409 DEPOSIT_NOT_CONFIRMED：闸门提示（后端 message 原样透传）
      message.error(errMsg(err, '该成交尚未确认两押一租到账'))
    } finally {
      setSubmitting(false)
    }
  }

  const submitReceipt = async () => {
    if (!receiptTarget) return
    const v = await receiptForm.validateFields()
    setSubmitting(true)
    try {
      await post(`/admin/rewards/${receiptTarget.id}/receipt`, { receiptUrl: v.receiptUrl })
      message.success('签收单已上传存档')
      setReceiptTarget(null)
      fetchList()
    } catch (err: unknown) {
      message.error(errMsg(err, '签收单上传失败'))
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * 入驻商家渠道判定（三栏恒 0 且置灰）：
   * 优先用后端派生 displayMode==='coupon'；历史行无该字段时回落「券采购额非空且三栏均 0/null」。
   */
  const isCouponChannel = (r: RewardItem): boolean => {
    if (r.displayMode) return r.displayMode === 'coupon'
    const cashish = [r.grossAmount, r.taxAmount, r.netAmount].some(
      (x) => x != null && Number(x) !== 0,
    )
    return !cashish && r.couponAmount != null
  }

  /** 三栏单元格：merchant 行 ¥0 置灰；历史行无值时回落 amount（语义 = 应发税前） */
  const renderMoneyCell = (r: RewardItem, kind: 'gross' | 'tax' | 'net') => {
    if (r._pending) {
      return <Text type="secondary">预估 {fmtMoney(r.amount)}</Text>
    }
    if (isCouponChannel(r)) {
      // 商家渠道金额口径＝券采购额，不走现金三栏（PDF 7.1/7.2）
      return <Text style={{ color: '#999' }}>{fmtMoney(0)}</Text>
    }
    const raw = kind === 'gross'
      ? r.grossAmount ?? r.amount
      : kind === 'tax'
        ? r.taxAmount
        : r.netAmount
    if (raw == null) return <Text type="secondary">—</Text>
    const node = <Text strong={kind !== 'tax'} style={{ fontSize: 12 }}>{fmtMoney(raw)}</Text>
    // 应发栏保留「调整前原值」划线（既有口径不变）
    if (kind === 'gross' && r.originalAmount != null && Number(r.originalAmount) !== Number(r.amount)) {
      return (
        <span>
          {node}
          <Text type="secondary" delete style={{ fontSize: 12, marginLeft: 6 }}>
            {fmtMoney(r.originalAmount)}
          </Text>
        </span>
      )
    }
    return node
  }

  const columns: ColumnsType<RewardItem> = [
    {
      title: '答谢ID',
      dataIndex: 'id',
      key: 'id',
      // 待核算虚拟行尚无奖励单，id 为负数（= -leadId），不能显示成 #R--5
      render: (v: number, r) => (r._pending ? <Text type="secondary">待生成</Text> : `#R-${v}`),
    },
    {
      title: '推荐人',
      key: 'referrer',
      render: (_: unknown, r) =>
        r.referrer ? (
          <span>
            {r.referrer.name}
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>
              ({r.referrer.rid})
            </Text>
          </span>
        ) : (
          `#${r.referrerId}`
        ),
    },
    {
      title: '客户线索',
      key: 'lead',
      render: (_: unknown, r) =>
        r.lead ? `${r.lead.name}（${r.lead.phone}）` : `#${r.leadId}`,
    },
    // thankyou_v2（批次 8.2）：单列「应发金额」拆为财务三栏（设计契约逐字标题）
    {
      title: '应发(税前)',
      dataIndex: 'amount',
      key: 'grossAmount',
      width: 130,
      render: (_: unknown, r) => renderMoneyCell(r, 'gross'),
    },
    {
      title: '代扣个税(20%)',
      key: 'taxAmount',
      width: 120,
      render: (_: unknown, r) => renderMoneyCell(r, 'tax'),
    },
    {
      title: '实发',
      key: 'netAmount',
      width: 120,
      render: (_: unknown, r) => renderMoneyCell(r, 'net'),
    },
    {
      title: '套档结果',
      dataIndex: 'couponTierResult',
      key: 'couponTierResult',
      width: 140,
      // TS1016 修复：可选参数后不能跟必选参数 —— 改为必选可空标注（语义不变）
      render: (v: string | null | undefined, r) => {
        if (v) return <Text style={{ fontSize: 12 }}>{v}</Text>
        return <Text type="secondary">{isCouponChannel(r) ? '待套档' : '—'}</Text>
      },
    },
    {
      title: '券采购额',
      dataIndex: 'couponAmount',
      key: 'couponAmount',
      width: 110,
      render: (v?: number | string | null) =>
        v == null ? <Text type="secondary">—</Text> : <Text strong style={{ fontSize: 12 }}>{fmtMoney(v)}</Text>,
    },
    {
      title: '采购单号/发票号',
      key: 'couponPurchaseNo',
      width: 170,
      render: (_: unknown, r) => {
        const po = r.couponPurchaseNo
        const fp = r.merchantInvoiceNo
        if (!po && !fp) return <Text type="secondary">—</Text>
        return (
          <Tooltip title={`采购单号：${po ?? '—'}｜商家发票号：${fp ?? '—'}`} placement="topLeft">
            <Text style={{ fontSize: 12 }}>{[po, fp].filter(Boolean).join(' / ')}</Text>
          </Tooltip>
        )
      },
    },
    {
      title: '券发放',
      key: 'couponIssue',
      width: 150,
      render: (_: unknown, r) => {
        if (!r.couponIssuedAt && !r.couponNoOrProof) {
          return <Text type="secondary">{isCouponChannel(r) ? '未登记' : '—'}</Text>
        }
        const st = SETTLEMENT_STATUS_LABELS[r.settlementStatus ?? '']
        return (
          <Space size={4} wrap>
            <Tag color={st?.color ?? 'default'}>{st?.label ?? (r.settlementStatus ?? '未结算')}</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.couponIssuer ?? '—'} · {fmtTime(r.couponIssuedAt)}
            </Text>
          </Space>
        )
      },
    },
    {
      title: '成交业态',
      key: 'dealCategory',
      width: 100,
      render: (_: unknown, r) => <Text style={{ fontSize: 12 }}>{dealCategoryText(r)}</Text>,
    },
    {
      title: '推荐人答谢',
      dataIndex: 'referrerRewardText',
      key: 'referrerRewardText',
      width: 200,
      ellipsis: { showTitle: false },
      render: (v?: string | null) =>
        v ? (
          <Tooltip title={v} placement="topLeft">
            <Text style={{ fontSize: 12 }}>{v}</Text>
          </Tooltip>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: '被推荐人礼遇',
      dataIndex: 'referredBenefitText',
      key: 'referredBenefitText',
      width: 200,
      ellipsis: { showTitle: false },
      render: (v?: string | null) =>
        v ? (
          <Tooltip title={v} placement="topLeft">
            <Text style={{ fontSize: 12 }}>{v}</Text>
          </Tooltip>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: '发放状态',
      dataIndex: 'reviewStatus',
      key: 'reviewStatus',
      render: (v: string) => (
        <Tag color={REWARD_REVIEW_LABELS[v]?.color ?? 'default'}>
          {REWARD_REVIEW_LABELS[v]?.label ?? v}
        </Tag>
      ),
    },
    {
      title: '签收信息',
      key: 'pay',
      render: (_: unknown, r) =>
        r.reviewStatus === 'paid' ? (
          <Text style={{ fontSize: 12 }}>
            {PAY_METHOD_LABELS[r.payMethod ?? ''] ?? '—'}
            {r.transactionNo ? ` · ${r.transactionNo}` : ''}
            {r.signedAt ? ` · ${fmtDate(r.signedAt)} 签收` : ''}
          </Text>
        ) : (
          '—'
        ),
    },
    {
      title: '签收单',
      key: 'receipt',
      render: (_: unknown, r) =>
        r.receiptUrl ? (
          <Space size={4}>
            <Tag color="success">已上传</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>{fmtTime(r.receiptUploadedAt)}</Text>
          </Space>
        ) : (
          <Tag>未上传</Tag>
        ),
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      render: (_: unknown, r) =>
        // 待核算虚拟行不是真实奖励单（id 为负数），任何针对 reward 的操作都会 404，
        // 故只提示财务下一步该做什么，不渲染调整/审批/发放/签收单按钮。
        r._pending ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r._depositConfirmed ? '点击上方一键核算' : '待录入两押一租到账'}
          </Text>
        ) : (
        <Space size={4} wrap>
          {isFinanceManager && r.reviewStatus !== 'paid' && (
            <Button
              size="small"
              style={{ minHeight: 32 }}
              onClick={() => {
                setAmountTarget(r)
                amountForm.setFieldsValue({
                  // 现金线索：回显 grossAmount（小程序读的字段），无则回落 amount；netAmount 回显
                  grossAmount: r.grossAmount != null ? Number(r.grossAmount) : Number(r.amount),
                  netAmount: r.netAmount != null ? Number(r.netAmount) : undefined,
                  // 商家券线索：回显券额度 / 套档结果
                  couponAmount: r.couponAmount != null ? Number(r.couponAmount) : undefined,
                  couponTierResult: r.couponTierResult ?? undefined,
                  reason: '',
                  // 回显当前文字快照，不改动则原值回传
                  referrerRewardText: r.referrerRewardText ?? undefined,
                  referredBenefitText: r.referredBenefitText ?? undefined,
                })
              }}
            >
              {/* thankyou_v2（批次 8.2）：「调整金额」→「调整答谢」（文本 + 金额均在此调） */}
              调整答谢
            </Button>
          )}
          {/* thankyou_v2：券发放登记（发放人自动取当前账号，后端写 couponIssuer） */}
          {canOperate && (
            <Button
              size="small"
              style={{ minHeight: 32 }}
              onClick={() => {
                setCouponTarget(r)
                couponForm.setFieldsValue({
                  couponIssuedAt: r.couponIssuedAt ? dayjs(r.couponIssuedAt) : dayjs(),
                  couponNoOrProof: r.couponNoOrProof ? [r.couponNoOrProof] : [],
                  settlementStatus: r.settlementStatus ?? 'unsettled',
                })
              }}
            >
              券发放登记
            </Button>
          )}
          {canApprove && r.reviewStatus === 'pending_approve' && (
            <Button size="small" type="primary" style={{ minHeight: 32 }} onClick={() => handleApprove(r)}>
              审批
            </Button>
          )}
          {canOperate && r.reviewStatus === 'pending_pay' && (
            <Button
              size="small"
              type="primary"
              style={{ minHeight: 32, background: '#27AE60', borderColor: '#27AE60' }}
              onClick={() => {
                setPayTarget(r)
                payForm.resetFields()
              }}
            >
              发放
            </Button>
          )}
          {canOperate && r.reviewStatus === 'paid' && (
            <Button
              size="small"
              style={{ minHeight: 32 }}
              onClick={() => {
                setReceiptTarget(r)
                receiptForm.setFieldsValue({ receiptUrl: r.receiptUrl ?? '' })
              }}
            >
              {r.receiptUrl ? '更新签收单' : '上传签收单'}
            </Button>
          )}
        </Space>
        ),
    },
  ]

  return (
    <>
      <Space style={{ marginBottom: 14 }} wrap>
        <Text type="secondary">发放状态：</Text>
        <Select
          allowClear
          placeholder="全部"
          style={{ minWidth: 140 }}
          value={reviewStatus}
          onChange={setReviewStatus}
          options={Object.entries(REWARD_REVIEW_LABELS).map(([value, s]) => ({ value, label: s.label }))}
        />
        {canOperate && (
          <Button type="primary" onClick={handleCalc} loading={calcLoading} style={{ minHeight: 44 }}>
            一键核算
          </Button>
        )}
        <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExport} style={{ minHeight: 44 }}>
          台账导出 CSV
        </Button>
      </Space>
      <Table<RewardItem>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        scroll={{ x: 'max-content' }}
        style={{ background: '#fff', borderRadius: 12 }}
        pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }}
      />

      <Modal
        title={`调整答谢（#R-${amountTarget?.id ?? ''}）`}
        open={!!amountTarget}
        onOk={submitAmount}
        onCancel={() => setAmountTarget(null)}
        okText="确认调整"
        cancelText="取消"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          {amountTarget && isCouponChannel(amountTarget)
            ? '商家券渠道：调整券采购额 / 套档结果，小程序「我的推荐」券卡同步显示；调整全程留痕（reward_logs：调整前内容 / 调整后内容 / 原因 / 操作人 / 时间）。'
            : `应发（税前）原值 ${fmtMoney(amountTarget?.originalAmount)}；调整应发 / 实发后，小程序「我的推荐」金额卡同步显示（个税按 20% 口径自动算）；全程留痕。`}
        </Text>
        <Form form={amountForm} layout="vertical">
          {amountTarget && isCouponChannel(amountTarget) ? (
            <>
              {/* 商家券线索：小程序券卡读 couponAmount/couponTierResult，此处改这两项；现金 amount 不出现 */}
              <Form.Item
                name="couponAmount"
                label="券采购额（元，小程序券卡显示口径）"
                rules={[{ required: true, message: '券采购额必填' }]}
              >
                <InputNumber min={0} style={{ width: '100%', minHeight: 44 }} />
              </Form.Item>
              <Form.Item name="couponTierResult" label="套档结果（选填，小程序券卡显示）">
                <Input placeholder="例：面值 10,000 元餐饮代金券" maxLength={200} />
              </Form.Item>
            </>
          ) : (
            <>
              {/* 现金线索：小程序金额卡读 grossAmount/netAmount，此处同步改这两栏；个税 20% 口径提交时自动算 */}
              <Form.Item
                name="grossAmount"
                label="调整后应发（税前，元）"
                rules={[{ required: true, message: '应发（税前）必填' }]}
              >
                <InputNumber min={0} style={{ width: '100%', minHeight: 44 }} />
              </Form.Item>
              <Form.Item
                name="netAmount"
                label="调整后实发（税后，元）"
                rules={[{ required: true, message: '实发（税后）必填' }]}
              >
                <InputNumber min={0} style={{ width: '100%', minHeight: 44 }} />
              </Form.Item>
            </>
          )}
          {/* 三段 TextArea（设计契约）：调整原因 / 推荐人答谢 / 被推荐人礼遇 */}
          <Form.Item
            name="reason"
            label="调整原因（必填，强制留痕）"
            rules={[{ required: true, message: '调整原因必填' }]}
          >
            <Input.TextArea rows={3} placeholder="例：客户首期优惠 10%，答谢同步下调" />
          </Form.Item>
          <Form.Item name="referrerRewardText" label="推荐人答谢（选填，回显当前值）">
            <Input.TextArea rows={2} placeholder="例：首月租金 10% 答谢金（税前）" />
          </Form.Item>
          <Form.Item name="referredBenefitText" label="被推荐人礼遇（选填，回显当前值）">
            <Input.TextArea rows={2} placeholder="例：签约礼包 / 面值 20,000 元餐饮代金券" />
          </Form.Item>
        </Form>
      </Modal>

      {/* thankyou_v2（批次 8.2）：券发放登记弹窗 → POST /admin/rewards/:id/coupon-issue
          字段（设计契约）：发放人（自动当前账号，只读）· 发放时间 · 券号或凭证（Upload）· 结算状态 */}
      <Modal
        title={`券发放登记（#R-${couponTarget?.id ?? ''}）`}
        open={!!couponTarget}
        onOk={submitCouponIssue}
        onCancel={() => setCouponTarget(null)}
        okText="确认登记"
        cancelText="取消"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          {couponTarget?.couponTierResult
            ? `套档结果：${couponTarget.couponTierResult}；券采购额 ${fmtMoney(couponTarget.couponAmount)}。`
            : '登记发放人 / 发放时间 / 券号或凭证，作为项目方已履行交付义务的唯一证据（需求 8.2 第 19 条）。'}
        </Text>
        <Form form={couponForm} layout="vertical" initialValues={{ settlementStatus: 'unsettled' }}>
          <Form.Item label="发放人（自动当前账号）">
            <Input readOnly value={currentUser?.username ?? '—'} style={{ background: '#F5F7FA' }} />
          </Form.Item>
          <Form.Item name="couponIssuedAt" label="发放时间">
            <DatePicker showTime style={{ width: '100%', minHeight: 44 }} />
          </Form.Item>
          <Form.Item
            name="couponNoOrProof"
            label="券号或凭证照片（Upload）"
            extra="走 POST /admin/uploads/document（图片 / PDF）；取首个已上传文件作为凭证 URL"
          >
            <DocumentUploadList
              text="上传券号截图 / 发放凭证"
              hint="支持 JPG / PNG / GIF / PDF，单个 ≤ 10MB；也可直接粘贴凭证 URL"
            />
          </Form.Item>
          <Form.Item
            name="settlementStatus"
            label="结算状态"
            extra="按核销进度结算（需求 8.3）；未结算行进入「未核销券」报表汇总"
          >
            <Select
              options={[
                { value: 'unsettled', label: '未结算' },
                { value: 'settled', label: '已结算' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`发放答谢（#R-${payTarget?.id ?? ''} · 实发 ${fmtMoney(payTarget?.netAmount ?? payTarget?.amount)}）`}
        open={!!payTarget}
        onOk={submitPay}
        onCancel={() => setPayTarget(null)}
        okText="确认发放"
        cancelText="取消"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          闸门校验：成交须已确认「两押一租到账」，否则后端返回 409（该成交尚未确认两押一租到账）。
        </Text>
        <Form form={payForm} layout="vertical" initialValues={{ payMethod: 'transfer', signedAt: dayjs() }}>
          <Form.Item name="payMethod" label="发放方式" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'transfer', label: '转账' },
                { value: 'cash', label: '现金' },
              ]}
            />
          </Form.Item>
          <Form.Item name="transactionNo" label="转账流水号（现金可空）">
            <Input placeholder="例：20260825001234" />
          </Form.Item>
          <Form.Item name="signedAt" label="签收日期">
            <DatePicker style={{ width: '100%', minHeight: 44 }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`签收单存档（#R-${receiptTarget?.id ?? ''}）`}
        open={!!receiptTarget}
        onOk={submitReceipt}
        onCancel={() => setReceiptTarget(null)}
        okText="保存"
        cancelText="取消"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={receiptForm} layout="vertical">
          <Form.Item
            name="receiptUrl"
            label="签收单文件地址（URL）"
            rules={[{ required: true, message: '请填写签收单 URL' }]}
          >
            <Input placeholder="https://…/receipt.jpg" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default RewardsPage
